import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Theme definitions
const THEMES = {
  light: {
    name: 'Light',
    colors: {
      background: '#f8f9fa',      // Off-white instead of pure white
      surface: '#ffffff',
      text: '#2c3e50',            // Dark gray instead of pure black
      textSecondary: '#666666',
      border: '#e0e0e0',
      primary: '#3498db',
      accent: '#2980b9',
      hover: '#ecf0f1',
      shadow: 'rgba(0, 0, 0, 0.1)'
    }
  },
  dark: {
    name: 'Dark',
    colors: {
      background: '#1e1e2e',      // Deep blue-gray background
      surface: '#2d2d44',         // Elevated surface color
      text: '#e4e4e7',            // Bright text
      textSecondary: '#a1a1aa',   // Muted secondary text
      border: '#3f3f55',          // Subtle borders
      primary: '#7f95bf',         // Muted blue accent (reduced saturation)
      accent: '#f59e0b',          // Warm amber
      hover: '#383850',           // Hover state
      shadow: 'rgba(0, 0, 0, 0.3)'
    }
  },
  highContrast: {
    name: 'High Contrast',
    colors: {
      background: '#ffffff',      // Pure white for clarity
      surface: '#f0f0f0',
      text: '#000000',            // Pure black for max contrast
      textSecondary: '#333333',
      border: '#000000',
      primary: '#0000ff',         // Bright blue
      accent: '#ff0000',          // Bright red
      hover: '#e0e0e0',
      shadow: 'rgba(0, 0, 0, 0.3)'
    }
  }
};

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Get saved theme from localStorage, default to 'light'
    const saved = localStorage.getItem('app_theme');
    return saved || 'light';
  });

  // Save theme to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('app_theme', theme);
    // Apply theme to document
    applyThemeToDocument(theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    const themes = Object.keys(THEMES);
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setTheme(themes[nextIndex]);
  }, [theme]);

  const setThemeByName = useCallback((themeName) => {
    if (THEMES[themeName]) {
      setTheme(themeName);
    }
  }, []);

  return (
    <ThemeContext.Provider value={{
      theme,
      colors: THEMES[theme].colors,
      themeName: THEMES[theme].name,
      toggleTheme,
      setThemeByName,
      availableThemes: THEMES
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Design tokens per theme (card-bg, bg-primary, etc.) so cards/surfaces follow theme and don't stay white in dark mode
const THEME_TOKENS = {
  light: {
    '--bg-primary': '#F7F8FC',
    '--bg-secondary': '#EFF3F8',
    '--bg-tertiary': '#FAFBFC',
    '--bg-gradient': 'linear-gradient(135deg, #EFF3F8 0%, #E8EEF7 100%)',
    '--card-bg': '#FFFFFF',
    '--card-border': '#DFE1E6',
    '--card-shadow': '0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(140, 152, 164, 0.08)',
    '--card-shadow-hover': '0 4px 8px rgba(0, 0, 0, 0.08), 0 12px 32px rgba(140, 152, 164, 0.12)',
    '--accent-primary': '#2F6FD6',
    '--accent-primary-hover': '#285FC0',
    '--accent-text': '#172B4D',
    '--accent-text-subtle': '#6B778C',
  },
  dark: {
    '--bg-primary': '#1e1e2e',
    '--bg-secondary': '#252536',
    '--bg-tertiary': '#2d2d44',
    '--bg-gradient': 'linear-gradient(135deg, #1e1e2e 0%, #252536 100%)',
    '--card-bg': '#2d2d44',
    '--card-border': '#3f3f55',
    '--card-shadow': '0 1px 3px rgba(0, 0, 0, 0.2), 0 8px 24px rgba(0, 0, 0, 0.25)',
    '--card-shadow-hover': '0 4px 8px rgba(0, 0, 0, 0.25), 0 12px 32px rgba(0, 0, 0, 0.3)',
    '--accent-primary': '#5874ad',
    '--accent-primary-hover': '#4f6698',
    '--accent-text': '#e4e4e7',
    '--accent-text-subtle': '#a1a1aa',
  },
  highContrast: {
    '--bg-primary': '#ffffff',
    '--bg-secondary': '#f0f0f0',
    '--bg-tertiary': '#f5f5f5',
    '--bg-gradient': 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
    '--card-bg': '#f0f0f0',
    '--card-border': '#000000',
    '--card-shadow': '0 1px 3px rgba(0, 0, 0, 0.2)',
    '--card-shadow-hover': '0 4px 8px rgba(0, 0, 0, 0.25)',
    '--accent-primary': '#0000ff',
    '--accent-primary-hover': '#0000cc',
    '--accent-text': '#000000',
    '--accent-text-subtle': '#333333',
  },
};

// Apply theme colors and design tokens to CSS variables and document
const applyThemeToDocument = (themeName) => {
  const theme = THEMES[themeName];
  const root = document.documentElement;

  // Expose theme to CSS selectors
  root.setAttribute('data-theme', themeName);

  // Set --color-* from theme.colors
  Object.entries(theme.colors).forEach(([key, value]) => {
    root.style.setProperty(`--color-${key}`, value);
  });

  // Set design tokens (--card-bg, --bg-primary, etc.) so cards/surfaces follow theme
  const tokens = THEME_TOKENS[themeName];
  if (tokens) {
    Object.entries(tokens).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  }

  // Apply to body
  document.body.style.backgroundColor = theme.colors.background;
  document.body.style.color = theme.colors.text;
};

export default ThemeContext;
